import { readEdgeEnvelope, type InvokeLike } from '@/utils/edgeInvokeEnvelope';

export type { InvokeLike };

/**
 * TIMER V2 — 8G.2C.10/.11: WHAT ACTUALLY HAPPENED TO EACH PICKUP CONSEQUENCE.
 *
 * 8G.2C.9 made the four derived consequences durably convergent. 8G.2C.10
 * stopped the handler treating "the request executed" as "the consequence is in
 * place". 8G.2C.11 fixes where those results are READ from, and splits one
 * state that was doing two jobs.
 *
 * ── THE READING DEFECT (§3) ────────────────────────────────────────────────
 * The Edge envelope nests the payload under `data`, and `functions.invoke`
 * returns the whole body as its own `.data`, so the field lives at
 * `invoke.data.data.outcome`. C.10 read `invoke.data.outcome` — one level too
 * shallow — and its tests passed because the FIXTURE made the same mistake.
 *
 * MEASURED against a real local Edge call, pre-fix:
 *   a genuine CREATED_QUEUED  -> FAILED ("could not be confirmed")
 *   a genuine canceled: 5     -> ALREADY_SATISFIED (it saw no field, read 0)
 *
 * The second is worse than a wrong label: `Number(undefined ?? 0)` turns the
 * ABSENCE of evidence into the EVIDENCE of zero, so a successful suppression of
 * five reminders and a response that said nothing at all were indistinguishable.
 *
 * ── THE TAXONOMY DEFECT (§9/§10) ───────────────────────────────────────────
 * C.10 collapsed every problem into one `FAILED`, then gave it one instruction:
 * "reopen the ticket to retry". For a row whose durable status is `failed`,
 * that is false twice over. The writer is create-if-absent, so reconciliation
 * PRESERVES that row — replaying pickup cannot repair it — and the reminder was
 * scheduled successfully; it was the SEND that failed. Those need different
 * words, so `FAILED` is now split into a retryable and a non-retryable form.
 */
export type ConsequenceState =
  /** This reconciliation put it in place. */
  | 'SATISFIED'
  /** It was already in place — a retry, or a colleague got there first. */
  | 'ALREADY_SATISFIED'
  /** It already went out. Cannot be "scheduled" again. */
  | 'ALREADY_SENT'
  /**
   * The durable row is already `canceled`. That is the WHOLE claim.
   *
   * 8G.2C.11.1: this used to say "a person cancelled it". Nothing on the row
   * supports that. MEASURED — `reminder_queue` has no actor column at all
   * (`id, ticket_id, shop_id, reminder_type, channel, recipient, send_at,
   * status, suppression_reason, message_content, attempts, last_attempt_at,
   * sent_at, created_at, updated_at, is_fallback, original_channel, worker_id,
   * target_role`); `worker_id` is the delivery worker, not a canceller, and
   * `suppression_reason` is free text.
   *
   * And most cancellations are NOT human. `useReminderTriggers` is a
   * `useEffect` that cancels automatically on ticket state changes — "Ticket
   * paused", "Vehicle present at shop", "Vehicle returned", "Ticket
   * reactivated", "reactivate_at changed". A row cancelled by the automated
   * "Vehicle returned" path was reproduced end-to-end and is indistinguishable
   * here from one a person stopped.
   *
   * Either way reconciliation leaves it alone. The reason to preserve it is
   * that something already decided, not that a human did.
   */
  | 'ALREADY_CANCELED'
  /**
   * The durable row is already `suppressed`. A DIFFERENT durable status, kept
   * distinct even though neither state evidences an actor — collapsing them
   * would discard a real difference in the row to fix a problem in the prose.
   */
  | 'ALREADY_SUPPRESSED'
  /**
   * The consequence EXISTS; a previous delivery attempt failed. Reconciliation
   * preserves it, so replaying pickup will not fix it — it needs a resend,
   * which is a different authority and is deliberately not built here.
   */
  | 'PREVIOUS_SEND_FAILED'
  /**
   * The call failed, or its result could not be classified. Durable state is
   * unconfirmed, and replay is safe because the C.9 operations converge.
   */
  | 'UNCONFIRMED';

export interface ConsequenceResult {
  state: ConsequenceState;
  /** Server-reported detail, for the operator message. Never invented. */
  detail?: string | null;
}

export interface PickupConsequences {
  suppression: ConsequenceResult;
  followup: ConsequenceResult;
  escalation: ConsequenceResult;
  cascade: ConsequenceResult;
}

/** Does this outcome label agree with the durable status returned beside it? */
function outcomeMatchesStatus(outcome: string, status: unknown): boolean {
  if (typeof status !== 'string') return outcome === 'UNKNOWN';
  switch (outcome) {
    case 'CREATED_QUEUED':
    case 'ALREADY_QUEUED':      return status === 'queued';
    case 'ALREADY_SENT':        return status === 'sent';
    case 'ALREADY_CANCELED':    return status === 'canceled';
    case 'ALREADY_SUPPRESSED':  return status === 'suppressed';
    case 'PREVIOUSLY_FAILED':   return status === 'failed';
    default:                    return false;
  }
}

/**
 * Classify a staff-enqueue response.
 *
 * The label is checked AGAINST the durable fields returned with it. A response
 * claiming `ALREADY_SENT` on a `queued` row, or `CREATED_QUEUED` on a `sent`
 * one, is self-contradictory and fails closed — the label is not trusted over
 * the state it claims to describe.
 */
export function classifyEnqueue(res: InvokeLike | null | undefined): ConsequenceResult {
  const env = readEdgeEnvelope(res);
  if (env.kind === 'TRANSPORT_ERROR') return { state: 'UNCONFIRMED', detail: env.detail };
  if (env.kind !== 'OK' || !env.payload) {
    return { state: 'UNCONFIRMED', detail: env.detail ?? 'unrecognised response' };
  }

  const p = env.payload;
  const outcome = typeof p.outcome === 'string' ? p.outcome : null;
  if (!outcome) {
    // Includes the pre-C.10 `{ queued: 1 }` payload, which says nothing about
    // the row even when correctly wrapped in a canonical envelope.
    return { state: 'UNCONFIRMED', detail: 'the reminder state could not be confirmed' };
  }

  if (outcome !== 'UNKNOWN' && !outcomeMatchesStatus(outcome, p.status)) {
    return { state: 'UNCONFIRMED',
             detail: `the response contradicts itself (${outcome} on a ${String(p.status)} row)` };
  }

  switch (outcome) {
    case 'CREATED_QUEUED':     return { state: 'SATISFIED' };
    case 'ALREADY_QUEUED':     return { state: 'ALREADY_SATISFIED' };
    case 'ALREADY_SENT':       return { state: 'ALREADY_SENT', detail: (p.sentAt as string) ?? null };
    case 'ALREADY_CANCELED':   return { state: 'ALREADY_CANCELED',
                                        detail: (p.suppressionReason as string) ?? null };
    case 'ALREADY_SUPPRESSED': return { state: 'ALREADY_SUPPRESSED',
                                        detail: (p.suppressionReason as string) ?? null };
    case 'PREVIOUSLY_FAILED':  return { state: 'PREVIOUS_SEND_FAILED' };
    default:                   return { state: 'UNCONFIRMED',
                                        detail: 'the reminder state could not be confirmed' };
  }
}

/**
 * Classify the pre-pickup suppression call.
 *
 * `canceled` must be genuinely present and a non-negative integer. MISSING IS
 * NOT ZERO: C.10 used `Number(d.canceled ?? 0)`, so a response carrying no
 * evidence at all was read as "cancelled nothing, all good".
 */
export function classifySuppression(res: InvokeLike | null | undefined): ConsequenceResult {
  const env = readEdgeEnvelope(res);
  if (env.kind === 'TRANSPORT_ERROR') return { state: 'UNCONFIRMED', detail: env.detail };
  if (env.kind !== 'OK' || !env.payload) {
    return { state: 'UNCONFIRMED', detail: env.detail ?? 'unrecognised response' };
  }

  const n = env.payload.canceled;
  if (typeof n !== 'number' || !Number.isInteger(n) || n < 0) {
    return { state: 'UNCONFIRMED', detail: 'the suppression result was not reported' };
  }
  // Zero is a perfectly good observed value — a retry, or a ticket that never
  // had pre-pickup reminders.
  return n > 0 ? { state: 'SATISFIED' } : { state: 'ALREADY_SATISFIED' };
}

/** Classify the board cascade, which resolves `{ ok }` rather than throwing. */
export function classifyCascade(
  res: { ok: boolean; error?: string } | null | undefined,
): ConsequenceResult {
  if (!res) return { state: 'UNCONFIRMED', detail: 'no result' };
  return res.ok ? { state: 'SATISFIED' } : { state: 'UNCONFIRMED', detail: res.error ?? null };
}

/** States that reconciliation can safely be run again against. */
export const isRetryable = (r: ConsequenceResult): boolean => r.state === 'UNCONFIRMED';

/** States where nothing is wrong and nothing needs doing. */
const isSettled = (r: ConsequenceResult): boolean =>
  r.state === 'SATISFIED' || r.state === 'ALREADY_SATISFIED'
  || r.state === 'ALREADY_SENT' || r.state === 'ALREADY_CANCELED'
  || r.state === 'ALREADY_SUPPRESSED';

/** Did every consequence land somewhere settled? */
export const allConsequencesOk = (c: PickupConsequences): boolean =>
  isSettled(c.suppression) && isSettled(c.followup)
  && isSettled(c.escalation) && isSettled(c.cascade);

/**
 * One concise, TRUE sentence about the derived consequences.
 *
 * Every positive claim is backed by an observed state. Retryable failures and
 * a previous SEND failure are reported separately, because only the first can
 * be fixed by reopening the ticket — telling an operator to retry a pickup to
 * repair a failed delivery would send them after something that cannot work.
 */
export function describeConsequences(c: PickupConsequences): string {
  const retry: string[] = [];
  if (isRetryable(c.suppression)) retry.push('their earlier reminders may still be active');
  if (isRetryable(c.followup)) retry.push('the follow-up could not be scheduled');
  if (isRetryable(c.escalation)) retry.push('the owner escalation could not be scheduled');
  if (isRetryable(c.cascade)) retry.push('the board needs recalculating');

  // Not retryable here: the row exists and reconciliation preserves it.
  const attention: string[] = [];
  if (c.followup.state === 'PREVIOUS_SEND_FAILED') {
    attention.push('the follow-up was scheduled earlier but its send attempt failed');
  }
  if (c.escalation.state === 'PREVIOUS_SEND_FAILED') {
    attention.push('the owner escalation was scheduled earlier but its send attempt failed');
  }

  if (retry.length > 0 && attention.length > 0) {
    return `Pickup recorded, but ${joinList(retry)} — reopen the ticket to retry. Separately, `
      + `${joinList(attention)} and needs attention.`;
  }
  if (retry.length > 0) {
    return `Pickup recorded, but ${joinList(retry)} — reopen the ticket to retry.`;
  }
  if (attention.length > 0) {
    return `Pickup recorded. ${capitalise(joinList(attention))} and needs attention.`;
  }

  // Nothing failed. Say what the reminders actually ARE — which is not always
  // "scheduled".
  const notes: string[] = [];
  notes.push(...describeSettled('follow-up', c.followup));
  notes.push(...describeSettled('owner escalation', c.escalation));

  if (notes.length === 0) {
    return 'Vehicle removed from schedule. Follow-up reminders are scheduled.';
  }
  const scheduled = [c.followup, c.escalation].some(
    (r) => r.state === 'SATISFIED' || r.state === 'ALREADY_SATISFIED');
  return scheduled
    ? `Vehicle removed from schedule. Follow-up reminders are scheduled, though ${joinList(notes)}.`
    : `Vehicle removed from schedule, but ${joinList(notes)}.`;
}

function describeSettled(label: string, r: ConsequenceResult): string[] {
  switch (r.state) {
    case 'ALREADY_SENT':
      return [`the ${label} was already sent`];
    case 'ALREADY_CANCELED':
      // ACTOR-NEUTRAL. The row is canceled; who or what cancelled it is not
      // recorded, and most cancellations come from automatic lifecycle paths.
      return [`the ${label} was already canceled and was left that way`];
    case 'ALREADY_SUPPRESSED':
      // A different durable status, described as itself. 8G.2C.10 called this
      // one "cancelled" too, which asserted both the wrong status and an actor.
      return [`the ${label} was already suppressed and was left that way`];
    default:
      return [];
  }
}

function joinList(items: string[]): string {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
